import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography
} from '@material-ui/core'
import { components } from 'cng-web-lib'
import { BasePage as CngBasePage } from 'cng-web-lib'
import { useForm, FormProvider } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import SnackBar from 'src/components/era-package/era/SnackBar'
import CngSection from '../../../components/cngcomponents/CngSection'
import { FileForUserGetCurrentUserId } from 'src/common/FileForUserCommon'

const {
  button: { CngButton },
  form: {
    field: { CngTextField }
  },
  CngGridItem
} = components

function UserSettingsContent({ showNotification }) {
  const methods = useForm({
    defaultValues: {
      mpciPartyId: '',
      filingOperator: '',
      email: ''
    }
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Fetch on mount — GET /mpci/user-settings/get?userId=<userId>
  useEffect(() => {
    const userId = FileForUserGetCurrentUserId()
    if (!userId) {
      setIsLoading(false)
      return
    }

    MpciApiClient.get(
      `${UaeMpciApiUrls.USER_SETTINGS_GET}?userId=${userId}`,
      (data) => {
        setIsLoading(false)
        if (data) {
          methods.reset({
            mpciPartyId: data.mpciPartyId || '',
            filingOperator: data.filingOperator || '',
            email: data.email || ''
          })
        }
      },
      (error) => {
        setIsLoading(false)
        console.error('[UserSettings] fetch failed', error)
        setErrorMessage('Failed to load user settings. Please try again.')
        setShowErrorSnackbar(true)
      }
    )
  }, [])

  const handleSubmit = methods.handleSubmit((formData) => {
    setIsSaving(true)
    setErrorMessage('')
    setShowErrorSnackbar(false)

    const userId = FileForUserGetCurrentUserId()
    const payload = {
      userId,
      mpciPartyId: formData.mpciPartyId || null,
      filingOperator: formData.filingOperator || null,
      email: formData.email || null
    }

    // PUT /mpci/user-settings/update — upsert (create or update)
    MpciApiClient.put(
      UaeMpciApiUrls.USER_SETTINGS_UPDATE,
      payload,
      (data) => {
        setIsSaving(false)
        if (data) {
          methods.reset({
            mpciPartyId: data.mpciPartyId || '',
            filingOperator: data.filingOperator || '',
            email: data.email || ''
          })
        }
        setShowSuccessSnackbar(true)
      },
      (error) => {
        setIsSaving(false)
        console.error('[UserSettings] save failed', error)
        setErrorMessage('Failed to save user settings. Please try again.')
        setShowErrorSnackbar(true)
      }
    )
  })

  return (
    <>
      <SnackBar
        title='Saved successfully'
        type='success'
        open={showSuccessSnackbar}
        onClose={() => setShowSuccessSnackbar(false)}
        duration={3000}
      >
        User settings saved successfully.
      </SnackBar>

      <SnackBar
        title='Error'
        type='error'
        open={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
      >
        {errorMessage}
      </SnackBar>

      {isLoading && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      )}

      {!isLoading && (
        <Card>
          <CardContent>
            <Box mb={2}>
              <Typography variant='h6'>MPCI User Settings</Typography>
              <Typography variant='body2' color='textSecondary'>
                Configure your default filing details for MPCI submissions.
              </Typography>
            </Box>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit}>
                <CngSection
                  title='Filing Information'
                  icon={<FontAwesomeIcon icon={['fal', 'sliders-h']} />}
                >
                  <Grid container spacing={2}>
                    <CngGridItem xs={12} sm={6}>
                      <CngTextField
                        name='mpciPartyId'
                        label='MPCI Party ID'
                        size='small'
                        inputProps={{ maxLength: 50 }}
                      />
                    </CngGridItem>

                    <CngGridItem xs={12} sm={6}>
                      <CngTextField
                        name='filingOperator'
                        label='Filing Operator'
                        size='small'
                        inputProps={{ maxLength: 3 }}
                      />
                    </CngGridItem>

                    <CngGridItem xs={12} sm={6}>
                      <CngTextField
                        name='email'
                        label='Email (for email notifications)'
                        size='small'
                        inputProps={{ maxLength: 255 }}
                      />
                    </CngGridItem>
                  </Grid>

                  <Box mt={3}>
                    <CngButton
                      type='submit'
                      color='primary'
                      size='medium'
                      disabled={isSaving}
                      shouldShowProgress={isSaving}
                      startIcon={<FontAwesomeIcon icon={['fal', 'save']} />}
                    >
                      {isSaving ? 'Saving...' : 'Save Settings'}
                    </CngButton>
                  </Box>
                </CngSection>
              </form>
            </FormProvider>
          </CardContent>
        </Card>
      )}
    </>
  )
}

function UserSettingsPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  return (
    <CngBasePage
      renderPage={(showSnackbar) => (
        <UserSettingsContent showNotification={showSnackbar} {...props} />
      )}
      extraCngBreadcrumbNameMap={breadcrumbNameMap}
    />
  )
}

export default UserSettingsPage